import { memo, useEffect, useMemo, useRef } from "react";
import {
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { AnalysisFeature } from "@/types";
import type { MZRTData } from "@/types/spectrum";
import {
  calculateRI,
  identifiedPointColor,
  MEASURED_ONLY_COLOR,
} from "@/utils/featureMapping";
import {
  enlargedDotRadius,
  mzDomain,
  paintScanHighlight,
  plotRectWithin,
  projectScanMarks,
  renderedPointRadius,
} from "@/utils/mzrtOverlay";
import {
  computeXAxisScale,
  formatXAxisTick,
  type XAxisScale,
} from "@/utils/mzrtXAxisScale";
import type { MZRTChartDataPoint, MZRTChartProps } from "./MZRTChartAdapter";
import MZRTSelectionOverlay from "./MZRTSelectionOverlay";

// 툴팁 내용 렌더링
const renderTooltipContent = (
  data: MZRTChartDataPoint,
  xAxisType: "rt" | "ri",
  features?: AnalysisFeature[],
) => {
  const matched = data.feature_id
    ? features?.find((f) => f.feature_id === data.feature_id)
    : undefined;
  const compoundName =
    matched?.predicted_substance && matched.predicted_substance !== "Unknown"
      ? matched.predicted_substance
      : null;
  const jsd = matched?.jsd_distance;
  const ri = xAxisType === "ri" ? data.ri : calculateRI(data.rt);

  return (
    <div
      key={`${data.feature_id}-${data.rt}-${data.mz}`}
      className="animate-pop-in bg-white p-3 border border-gray-200 rounded-lg shadow-lg min-w-[180px]"
    >
      {matched && (
        <div className="text-sm font-semibold text-blue-700 mb-1 flex items-center gap-1">
          <span>▸</span>
          <span>
            {compoundName ?? "Unknown"}
            <span className="ml-1 text-xs font-normal text-gray-500">
              ({data.feature_id})
            </span>
          </span>
        </div>
      )}
      <div className="text-sm font-medium text-gray-900">
        m/z {data.mz}
        <span className="ml-2 text-gray-500 font-normal">
          · RT {data.rt.toFixed(2)}분
        </span>
      </div>
      {ri != null && (
        <div className="text-xs text-gray-500">RI {Math.round(ri)}</div>
      )}
      <div className="text-xs text-gray-600 mt-1">
        Intensity {data.intensity.toFixed(2)}
      </div>
      {jsd != null && (
        <div className="text-xs text-gray-600">JSD {jsd.toFixed(3)}</div>
      )}
      {!matched && (
        <div className="text-xs text-gray-400 mt-1 italic">
          시료 측정 포인트
        </div>
      )}
    </div>
  );
};

// 2026-09-03: 종전에는 Tooltip cursor 로 세로 점선(VerticalCursor)을 그렸다.
// 그 선은 **툴팁이 뜰 때만**, 즉 점에 스냅됐을 때만 나타났다 — 운영자 정리와
// 정반대다("이동은 점선이 표시되고 스냅 걸리면 점선은 없어도 된다").
// 이제 이동 중 점선은 DOM 오버레이가 그리고, 스냅 시에는 링 강조와 요약 칩만
// 남긴다. recharts 자체 커서는 끈다(cursor={false}).

/** 근접 호버 스냅 반경(px) — TIC 과 같은 값·같은 의미(화면 픽셀 기준). */
const HOVER_SNAP_PX = 20;

/**
 * x축 라벨이 들어갈 하단 여백(px). 2026-09-06 추가.
 *
 * 이 플롯은 RT ↔ RI 토글이 있는데 x축에 라벨이 없어, 축만 봐서는 무엇이
 * 그려졌는지 알 수 없었다(운영자 제기). 라벨은 여백 없이는 보이지 않는다.
 */
const X_LABEL_GUTTER = 24;

/**
 * 차트 높이(px). 라벨 여백만큼 올려, 그림 영역은 종전과 같게 유지한다.
 * (280 = 종전 값, + X_LABEL_GUTTER − 종전 하단 기본 여백 5)
 */
const CHART_HEIGHT = 280 + X_LABEL_GUTTER - 5;

/** 점 크기를 못 읽었을 때의 대비값 — recharts 기본 심볼(면적 64)의 반지름. */
const DOT_RADIUS_FALLBACK = 4.51;

/**
 * 커서선 색 — **TIC(uPlot) 커서선과 같은 값**이다(실측 `1px dashed
 * rgb(96,125,139)`). 두 플롯이 RT 축을 공유하므로 커서 표기도 하나로 맞춘다.
 */
const CURSOR_LINE_COLOR = "#607d8b";
/**
 * AI 후보가 기록된 스캔에 붙었을 때의 선 — 더 진하고 굵다.
 *
 * 2026-09-03 (운영자): "분석이 안 된 점 위에 호버됐을 때와, 분석되어 클릭
 * 가능한 점의 표시 차이가 필요해 보인다."
 *
 * 이 플롯에는 두 종류의 스캔이 섞여 있다 — AI 후보가 기록된 스캔(분석 326
 * 기준 20건)과 측정만 있고 후보가 없는 스캔(57건). **둘 다 누를 수 있지만
 * 결과가 다르다**: 후보가 있으면 그 후보가 선택되고 표·상세가 채워지지만,
 * 없으면 물질 선택이 해제되고 그 스캔의 질량 스펙트럼만 보게 된다.
 * 누르기 전에 어느 쪽인지 알 수 있어야 한다.
 *
 * 그래서 선을 3단으로 나눈다 —
 *   이동 중(점선·옅음) → 후보 없는 스캔(실선·옅음) → 후보 있는 스캔(실선·진함)
 * 칩 문구도 함께 달라진다.
 */
const SNAP_LINE_COLOR = "#334155";

/** RT 비교 키 — 부동소수 오차로 같은 스캔이 갈리지 않게 3자리 고정.
    컴포넌트 밖 순수 함수 (안에 두면 매 렌더 새 함수가 되어 useMemo 가 무의미). */
const rtKey = (rt: number) => rt.toFixed(3);

/**
 * MZRTChartRecharts — MZRTChartProps 인터페이스를 구현하는 Recharts ScatterChart.
 *
 * 순수 시각화 컴포넌트: data transform, state 관리는 MZRTPlot에서 수행.
 * Phase E에서 MZRTChartECharts로 교체 시 이 파일을 대체.
 */
export const MZRTChartRecharts = memo(
  ({
    data,
    featureMaxIntensities,
    xAxisType,
    onClick,
    features,
  }: MZRTChartProps) => {
    const xAxisDataKey = xAxisType === "rt" ? "rt" : "ri";

    // 스냅된 스캔 — **React 상태가 아니라 ref 다.** (2026-09-03)
    //
    // 상태로 두면 값이 바뀔 때마다 산점도가 통째로 다시 만들어진다. 실측:
    // 스냅 1회당 307ms(점 485개 · SVG 요소 1,117개 재생성), 마우스로 좌→우
    // 한 번 훑으면 80회 — dev 스윕 21.6초 중 18.7초(86%) 동안 메인 스레드가
    // 막혔다. 그래서 운영자에게는 "점선은 즉시 따라오는데 링만 늦게 뜬다"로
    // 보였다(점선·라벨은 처음부터 DOM 을 직접 갱신해 1~3ms).
    //
    // 같은 화면의 TIC 은 같은 조작을 스냅 1회당 0.83ms 로 처리한다. 차이는
    // 대상 개수(20 vs 77)가 아니라 **한 건을 어떻게 처리하는가**였다.
    const hoveredRTRef = useRef<number | null>(null);

    // 2026-09-03 (렌더 비용): 아래 세 목록은 이전에 **렌더할 때마다** 다시
    // 계산됐다. 특히 라벨 점은 관찰 수만큼 반복 filter 해서 관찰 170건 ×
    // 점 1,700개 = 약 29만 회 비교를 매 렌더(= 마우스 호버마다) 수행했다.
    // data 가 그대로면 재계산하지 않는다.
    const unlabeledPoints = useMemo(
      () => data.filter((item) => !item.feature_id),
      [data],
    );
    const labeledPoints = useMemo(
      () => data.filter((item) => item.feature_id),
      [data],
    );

    // 점별 색(스캔 내 상대강도 농도). 라벨 점이 바뀔 때만 다시 만든다.
    const labeledCells = useMemo(
      () =>
        labeledPoints.map((entry, index) => (
          <Cell
            // index 를 넣는 이유: 같은 스캔에서 m/z 가 소수 1자리로 반올림되면
            // (feature_id, rt, mz) 가 겹친다 — 실측 '상세' 밀도에서 라벨 점
            // 1,180개 중 2건 충돌(React duplicate key 경고). 재조정에서 Cell 이
            // 하나 누락되면 recharts 는 Cell 을 **인덱스로** 점에 배정하므로 그
            // 지점 이후 모든 점의 강도색이 한 칸씩 밀린다 — 증거 화면에서 점
            // 색이 조용히 틀리는 경로다. (이번 변경 이전부터 있던 결함)
            key={`cell-${index}-${entry.feature_id}-${entry.rt}-${entry.mz}`}
            fill={identifiedPointColor(
              entry.intensity,
              featureMaxIntensities[entry.feature_id] ?? 0,
            )}
          />
        )),
      [labeledPoints, featureMaxIntensities],
    );

    // 2026-09-03 (운영자 요청): TIC 과 같은 조작 언어로 맞춘다.
    //
    // 종전에는 **점 위에 정확히** 올려야만(handlePointEnter) 같은 스캔 열이
    // 강조됐다. 마우스를 근처로 가져가도 아무 일이 없어 "누르면 무엇이 잡히는지"
    // 를 알 수 없었다. 이제 플롯 위 어디서든 가장 가까운 스캔을 찾아 **세로
    // 점선**으로 예고한다(TIC 의 관찰 점선과 같은 표기).
    //
    // 상태는 **대상이 바뀔 때만** 갱신한다 — 픽셀마다 setState 하면 점 수백 개
    // 짜리 차트를 매번 다시 그리게 된다.
    const wrapperRef = useRef<HTMLDivElement>(null);
    // 호버 강조를 그리는 덧그림 층 — 차트 밖이라 차트를 다시 만들지 않는다.
    const hoverOverlayRef = useRef<SVGSVGElement>(null);
    /** 현재 x축에서 이 점이 놓인 값(RT 또는 RI). */
    const axisValueOf = (point: MZRTChartDataPoint) =>
      xAxisType === "rt" ? point.rt : (point.ri ?? point.rt);

    // 커서 점선은 **React 상태 없이** DOM 을 직접 움직인다 — 마우스 이동마다
    // setState 하면 점 수백 개짜리 차트를 매번 다시 그리게 된다.
    const cursorLineRef = useRef<HTMLDivElement>(null);
    const cursorLabelRef = useRef<HTMLDivElement>(null);

    // 스캔별 요약 — 스냅 칩("F12 · RT 5.53 · 후보 3")에 쓴다.
    // features 는 후보 행 목록이므로 RT 로 묶어 개수를 센다.
    const scanSummary = useMemo(() => {
      const byRt = new Map<string, { featureId: string; count: number }>();
      for (const feature of features ?? []) {
        if (typeof feature.rt !== "number") continue;
        const key = rtKey(feature.rt);
        const found = byRt.get(key);
        if (found) {
          found.count += 1;
        } else {
          byRt.set(key, { featureId: feature.feature_id, count: 1 });
        }
      }
      return byRt;
    }, [features]);

    const xValues = useMemo(() => {
      const seen = new Set<number>();
      for (const point of data) {
        const value =
          xAxisType === "rt" ? point.rt : (point as { ri?: number }).ri;
        if (typeof value === "number") seen.add(value);
      }
      return [...seen].sort((a, b) => a - b);
    }, [data, xAxisType]);

    // 축 도메인 — **차트에 넘기는 것과 같은 규칙**이어야 덧그림 표식이 점 위에
    // 얹힌다. x 는 <XAxis domain> 에 주는 함수 그대로, m/z 는 <YAxis domain> 과
    // 같은 규칙(utils/mzrtOverlay 의 mzDomain)이다.
    // 축척은 **한 번만** 계산해 <XAxis> 와 덧그림 레이어가 같은 값을 쓴다.
    // 예전에는 XAxis 에 함수를 넘기고 여기서 같은 함수를 따로 호출했다 —
    // 두 값이 일치한 것은 recharts 가 넘겨주는 [dataMin,dataMax] 가 마침
    // xValues 의 양끝과 같았기 때문이고, 계약이 아니라 우연이었다.
    const xScale = useMemo<XAxisScale | null>(
      () =>
        xValues.length === 0
          ? null
          : computeXAxisScale(
              xAxisType,
              xValues[0],
              xValues[xValues.length - 1],
            ),
      [xValues, xAxisType],
    );
    const xDomain = xScale?.domain ?? null;
    const yDomain = useMemo(() => mzDomain(data), [data]);

    /**
     * 지금 눌리면 잡힐 스캔을 강조한다 — **리렌더 없음.**
     *
     * 2026-09-04 (운영자): "**분석이 완료된 도트에만** 마우스 호버 중에
     * 표시되면 좋겠다. 단순 동그라미 그리지 말고 **도트가 커지는 걸로**."
     *
     * 그래서 두 가지를 지킨다:
     *   · 후보가 기록된 스캔에서만 점을 강조한다(측정만 있는 스캔은 선만).
     *   · 강조는 테두리가 아니라 **같은 색으로 채운 더 큰 점**이다 — 원래 점을
     *     덮으므로 "점이 커졌다"로 보인다. 차트의 점 노드는 건드리지 않는다.
     */
    const applyHover = (next: number | null) => {
      const prev = hoveredRTRef.current;
      const sameTarget =
        (prev == null ? null : rtKey(prev)) ===
        (next == null ? null : rtKey(next));
      if (sameTarget) return;
      hoveredRTRef.current = next;

      // 커서 모양도 DOM 으로 바꾼다 — 이것 때문에 리렌더하면 본전이 없다.
      if (wrapperRef.current) {
        wrapperRef.current.style.cursor =
          next == null ? "crosshair" : "pointer";
      }

      // **동정된 점만** 강조 대상이다. 측정만 있는 스캔은 여기서 빈 배열이 된다.
      const analyzed =
        next == null
          ? []
          : labeledPoints.filter(
              (point) => rtKey(axisValueOf(point)) === rtKey(next),
            );

      const plot = plotRectWithin(wrapperRef.current);
      const radius = renderedPointRadius(wrapperRef.current);
      const marks =
        plot && radius && xDomain && yDomain
          ? projectScanMarks(
              analyzed.map((point) => ({
                x: axisValueOf(point),
                mz: point.mz,
                // 원래 점과 **같은 색** — 차트가 쓰는 계산을 그대로 쓴다.
                color: identifiedPointColor(
                  point.intensity,
                  featureMaxIntensities[point.feature_id] ?? 0,
                ),
              })),
              plot,
              xDomain,
              yDomain,
            )
          : [];

      paintScanHighlight(hoverOverlayRef.current, marks, {
        radius: enlargedDotRadius(radius ?? DOT_RADIUS_FALLBACK),
      });
    };

    // biome-ignore lint/suspicious/noExplicitAny: recharts Scatter onMouseEnter entry 타입 광범위
    const handlePointEnter = (entry: any) => {
      const point = (entry?.payload ?? entry) as MZRTChartDataPoint | undefined;
      if (!point || typeof point.rt !== "number") return;
      applyHover(axisValueOf(point));
    };

    // 데이터·축이 바뀌면 옛 강조는 뜻이 없다 — 남겨두면 엉뚱한 자리를 가리킨다.
    // data·xAxisType 은 본문에서 읽지 않고 **초기화 트리거**로만 쓴다.
    // biome-ignore lint/correctness/useExhaustiveDependencies: 위 사유
    useEffect(() => {
      hoveredRTRef.current = null;
      paintScanHighlight(hoverOverlayRef.current, [], { radius: 0 });
    }, [data, xAxisType]);

    const handleWrapperMove = (event: React.MouseEvent<HTMLDivElement>) => {
      const grid = wrapperRef.current?.querySelector(
        ".recharts-cartesian-grid",
      );
      if (!grid || xValues.length === 0) return;
      const rect = grid.getBoundingClientRect();
      const wrapperRect = wrapperRef.current?.getBoundingClientRect();
      const relX = event.clientX - rect.left;
      if (relX < 0 || relX > rect.width) {
        hideCursorLine();
        return;
      }

      if (!xDomain) return;
      const [domainMin, domainMax] = xDomain;
      const span = domainMax - domainMin;
      if (span <= 0) return;
      const cursorValue = domainMin + (relX / rect.width) * span;
      const pxPerUnit = rect.width / span;

      let nearest = xValues[0];
      let bestDistance = Math.abs(nearest - cursorValue);
      for (const value of xValues) {
        const distance = Math.abs(value - cursorValue);
        if (distance < bestDistance) {
          bestDistance = distance;
          nearest = value;
        }
      }
      // TIC 과 같은 20px 스냅 — 화면 픽셀 기준이라 확대·축소에 자동으로 맞춰진다.
      const withinSnap = bestDistance * pxPerUnit <= HOVER_SNAP_PX;
      applyHover(withinSnap ? nearest : null);

      // 두 상태를 **다른 표기**로 나눈다(운영자 정리 2026-09-03):
      //   · 이동 중(스냅 없음) → 커서 위치에 세로 점선 = "여기 RT 를 보고 있다"
      //   · 스냅됨           → 점선을 지우고 그 스캔의 점들만 강조 = "이게 잡힌다"
      // 둘을 동시에 그리면 무엇이 잡히는지 오히려 흐려진다.
      const line = cursorLineRef.current;
      const label = cursorLabelRef.current;
      if (!line || !label || !wrapperRect) return;

      const offsetLeft = rect.left - wrapperRect.left;
      const offsetTop = rect.top - wrapperRect.top;

      if (withinSnap) {
        // 스냅 — 선을 그 스캔에 붙여 **실선**으로 바꾸고, 무엇이 잡히는지 칩으로
        // 알린다. 이동 중(점선)과 스냅(실선)의 구분은 한 채널 안에서 유지된다.
        //
        // 후보가 기록된 스캔이면 더 진하고 굵게 — 누르면 결과가 다르기 때문이다
        // (후보 선택 vs 스펙트럼만 보기). 상세는 SNAP_LINE_COLOR 주석 참조.
        const summary = scanSummary.get(rtKey(nearest));
        line.style.display = "block";
        line.style.borderLeftStyle = "solid";
        line.style.borderLeftWidth = summary ? "2px" : "1px";
        line.style.borderLeftColor = summary
          ? SNAP_LINE_COLOR
          : CURSOR_LINE_COLOR;
        line.style.left = `${offsetLeft + (nearest - domainMin) * pxPerUnit}px`;
        line.style.top = `${offsetTop}px`;
        line.style.height = `${rect.height}px`;
        const unit = xAxisType === "rt" ? "RT" : "RI";
        label.textContent = summary
          ? `${summary.featureId} · ${unit} ${nearest.toFixed(2)} · 후보 ${summary.count}`
          : `${unit} ${nearest.toFixed(2)} · AI 후보 없음 · 스펙트럼만`;
        label.style.display = "block";
        label.style.left = `${offsetLeft + (nearest - domainMin) * pxPerUnit}px`;
        label.style.top = `${offsetTop - 4}px`;
        label.style.transform = "translate(-50%, -100%)";
        return;
      }

      // 이동 중 — 점선 + 커서 위치의 값
      line.style.display = "block";
      line.style.borderLeftStyle = "dashed";
      line.style.borderLeftWidth = "1px";
      line.style.borderLeftColor = CURSOR_LINE_COLOR;
      line.style.left = `${offsetLeft + relX}px`;
      line.style.top = `${offsetTop}px`;
      line.style.height = `${rect.height}px`;

      label.textContent = `${xAxisType === "rt" ? "RT" : "RI"} ${cursorValue.toFixed(2)}`;
      label.style.display = "block";
      label.style.left = `${offsetLeft + relX}px`;
      label.style.top = `${offsetTop - 4}px`;
      label.style.transform = "translate(-50%, -100%)";
    };

    /** 플롯 밖으로 나가거나 마우스가 떠나면 점선·라벨을 감춘다. */
    function hideCursorLine() {
      if (cursorLineRef.current) cursorLineRef.current.style.display = "none";
      if (cursorLabelRef.current) cursorLabelRef.current.style.display = "none";
    }

    /** 예고한 스캔을 실제로 고를 수 있게 — 미리보기만 있고 못 누르면 반쪽이다.
     *
     * 핸들러는 래퍼 div 가 아니라 **차트에** 단다. div 에 onClick 을 달면
     * 정적 요소를 상호작용 요소로 위장하게 되고(a11y 린트), 키보드로는
     * 좌표가 없어 실제로 쓸 수 없는 가짜 약속이 된다 — 키보드 선택은 관찰
     * 표가 담당한다(외부 자문 2026-09-03). */
    // biome-ignore lint/suspicious/noExplicitAny: recharts state 타입 광범위
    const handleChartClick = (_state: any, event?: React.SyntheticEvent) => {
      // 점을 정확히 눌렀으면 Scatter 의 onClick 이 처리한다(이중 처리 방지).
      if (
        (event?.target as HTMLElement | undefined)?.closest?.(
          ".recharts-symbols",
        )
      )
        return;
      const hovered = hoveredRTRef.current;
      if (hovered == null) return;
      const key = rtKey(hovered);
      const pick =
        data.find(
          (point) =>
            rtKey(
              xAxisType === "rt"
                ? point.rt
                : ((point as { ri?: number }).ri ?? point.rt),
            ) === key && point.feature_id,
        ) ??
        data.find(
          (point) =>
            rtKey(
              xAxisType === "rt"
                ? point.rt
                : ((point as { ri?: number }).ri ?? point.rt),
            ) === key,
        );
      if (pick) onClick?.(pick);
    };

    return (
      // 이 div 는 마우스 x 좌표로 '가까운 스캔'을 계산하기 위한 **기하 측정
      // 래퍼**다. 상호작용 요소가 아니다 — 클릭은 차트(ScatterChart onClick)가
      // 받고, 키보드 선택은 관찰 표가 담당한다. div 에 role/tabIndex 를 붙여
      // 버튼인 척하면 키보드 사용자에게 좌표 없는 가짜 약속이 된다
      // (외부 자문 2026-09-03).
      // biome-ignore lint/a11y/noStaticElementInteractions: 위 사유
      <div
        ref={wrapperRef}
        // 마우스 위치로 "가까운 스캔"을 계산하기 위한 래퍼일 뿐, 상호작용
        // 요소가 아니다 — 클릭은 차트가 받고 키보드 선택은 관찰 표가 맡는다.
        role="presentation"
        onMouseMove={handleWrapperMove}
        onMouseLeave={() => {
          applyHover(null);
          hideCursorLine();
        }}
        className="relative cursor-crosshair"
      >
        {/* 커서 세로 점선 — 스냅되지 않은 '이동 중' 상태에서만 보인다. */}
        <div
          ref={cursorLineRef}
          data-testid="mzrt-cursor-line"
          aria-hidden="true"
          /* 색·굵기·실선 여부는 상태에 따라 JS 가 정한다(TIC 커서선과 같은 색). */
          className="pointer-events-none absolute border-l border-dashed"
          style={{ display: "none" }}
        />
        {/* 값 라벨 — 이동 중에는 커서 RT, 스냅되면 잡힐 대상 요약.
            둘 다 DOM 을 직접 갱신한다(마우스마다 리렌더 금지). */}
        <div
          ref={cursorLabelRef}
          data-testid="mzrt-cursor-label"
          aria-hidden="true"
          className="pointer-events-none absolute z-10 whitespace-nowrap rounded border border-slate-200 bg-white/95 px-1.5 py-0.5 text-[10px] text-slate-600 shadow-sm"
          style={{ display: "none" }}
        />
        {/* 호버 강조(커진 점) — 차트 밖 덧그림 층이라 여기 그려도 산점도는
            다시 만들어지지 않는다. 마우스 이동 중에는 DOM 만 갱신한다. */}
        <svg
          ref={hoverOverlayRef}
          data-testid="mzrt-hover-overlay"
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full"
        >
          <title>마우스로 가리킨 스캔의 동정 점</title>
        </svg>
        {/* 선택 표시 — store 를 직접 구독하는 작은 컴포넌트가 그린다. 선택이
            바뀌어도 이 층만 다시 그려지고 산점도는 그대로다. */}
        <MZRTSelectionOverlay
          data={data}
          features={features}
          featureMaxIntensities={featureMaxIntensities}
          xAxisType={xAxisType}
          xDomain={xDomain}
          yDomain={yDomain}
        />
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          {/* margin.bottom 은 x축 라벨 자리다. 이걸 빼고 label 만 넣으면 라벨이
              SVG viewBox 밖(y = height + 0.71em)에 그려져 **화면에서 사라진다** —
              MassSpectrum·SpectrumComparison 이 실제로 그 상태였다(2026-09-06). */}
          <ScatterChart
            data={data}
            onClick={handleChartClick}
            // right 20 — interval={0} 이라 마지막 눈금을 항상 그리는데, 라벨의
            // 절반이 축 끝을 넘어간다. 5 로는 'RI 1800' 이 '180' 으로 잘렸다.
            margin={{ top: 5, right: 20, bottom: X_LABEL_GUTTER, left: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey={xAxisDataKey}
              type="number"
              domain={xScale?.domain ?? [0, 1]}
              tickCount={xScale?.tickCount ?? 2}
              // recharts 는 formatter 가 없으면 `min + i*step` 을 그대로 찍어
              // 8.200000000000001 같은 눈금이 나온다 (2026-09-06).
              tickFormatter={(value: number) =>
                formatXAxisTick(value, xScale?.decimals ?? 0)
              }
              tick={{ fontSize: 12, fill: "#64748b" }}
              // 눈금 수는 computeXAxisScale 이 이미 읽기 좋게 정한다(≤ 14).
              // 자동 솎기(equidistantPreserveStart)를 그대로 두면 그 위에서
              // 한 번 더 걸러 축에 눈금이 2개만 남았다(실측 2026-09-06).
              interval={0}
              label={{
                // 2026-09-06 (운영자): RI 는 영문 표기로. 국제 표준 용어가
                // "Retention Index" 이고 같은 축의 "RT (min)"·"m/z" 도 이미
                // 영문·기호다. (`.claude/rules/coding-conventions.md` 의
                // "Korean labels" 는 UI 문구 규칙이고, 축의 계량 단위·표준
                // 용어는 그 대상이 아니다.)
                value: xAxisType === "rt" ? "RT (min)" : "RI (Retention Index)",
                position: "insideBottom",
                offset: -8,
                style: { textAnchor: "middle", fill: "#64748b", fontSize: 12 },
              }}
            />
            <YAxis
              dataKey="mz"
              type="number"
              domain={[
                (dataMin: number) => Math.max(dataMin - 10, 0),
                "dataMax",
              ]}
              label={{
                value: "m/z",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#64748b" },
              }}
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <Tooltip
              // recharts 가 그리는 자체 커서선을 끈다 — 스냅 상태에서는
              // 점선이 없어야 '무엇이 잡히는지'가 흐려지지 않는다(운영자
              // 정리 2026-09-03). 이동 중 점선은 우리가 DOM 으로 그린다.
              cursor={false}
              isAnimationActive={false}
              offset={12}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                // feature 매칭 우선, 없으면 첫 payload (sample 직접 측정 포함)
                const featurePayload = payload.find(
                  (p) => (p.payload as MZRTChartDataPoint)?.feature_id,
                );
                const pointData = (featurePayload ?? payload[0])
                  .payload as MZRTChartDataPoint;
                return renderTooltipContent(pointData, xAxisType, features);
              }}
            />
            {/* 2026-09-06: 차트 안 도트 범례를 제거했다.
                범례는 "이 색 = F31" 이라고 말했지만 색 키가 실제로 없었다 —
                FEATURE_COLORS(featureMapping.ts)가 F1~F12 만 갖고 있어 F13
                이상은 전부 같은 fallback 회색이다(브라우저 실측: 칩 12개 중
                고유색 3개, 운영 분석은 관찰 170건 중 158건이 회색). 게다가
                칩은 100% 농도 원색을 그리는데 점은 램프 색이라, 색 키가
                있었더라도 칩과 점의 색이 같은 F 안에서 달랐다.
                개수 문장은 버리지 않고 MZRTPlot 의 차트 아래 형제 DOM 으로
                옮겼다 — 거기서는 `hidden > 0` 게이트 없이 항상 나오고, 범례
                높이가 플롯 높이를 깎는 recharts 피드백 루프(ChartUtils
                appendOffsetOfLegend)도 함께 끊긴다. */}
            {/* Sprint 22 회귀 fix: sample 직접 측정 포인트(feature_id="")는
              feature 색상 그룹과 매칭되지 않아 안 보이던 문제. 시료 fingerprint를
              그레이 배경으로 먼저 렌더, feature는 그 위에 오버레이.
              onClick: gray 포인트 클릭 시 그 스캔으로 이동하고 물질 선택은
              해제된다 — TIC 의 빈 구간 클릭과 같은 정책(2026-09-03 운영자
              결정 7-a). 처리는 store 의 selectMZRT 가 한다. */}
            <Scatter
              onClick={onClick as ((data: MZRTData) => void) | undefined}
              onMouseEnter={handlePointEnter}
              data={unlabeledPoints}
              dataKey="mz"
              fill={MEASURED_ONLY_COLOR}
              // r 은 recharts 가 무시한다 — 두 시리즈 모두 기본 심볼(면적 64
              // → 반지름 4.51px)로 그려진다(실측: utils/mzrtOverlay.ts:114-125).
              // 즉 두 부류를 가르는 것은 **색 하나뿐**이다. 크기로 가르려면
              // shape 를 써야 하는데, 그러면 renderedPointRadius 가 DOM 첫
              // 심볼(= 이 시리즈)만 읽어 강조 반지름이 엉뚱한 부류를 따라간다.
              r={3}
              name="시료 측정"
              isAnimationActive={false}
              legendType="none"
              activeShape={false}
            />
            {/* 2026-09-03: 관찰마다 Scatter 시리즈를 만들던 것(관찰 170건 =
              시리즈 170개)을 **단일 시리즈 + 점별 Cell 색**으로 합쳤다.
              점·색·클릭·툴팁 결과는 동일하고, 렌더당 반복 filter 와 시리즈
              오버헤드가 사라진다. (2026-09-06: 색은 더 이상 관찰별이 아니다 —
              부류(동정됨)를 색조로, 스캔 내 상대강도를 명도로 나른다.
              labeledCells 참조.) */}
            {labeledPoints.length > 0 && (
              <Scatter
                onClick={onClick as ((data: MZRTData) => void) | undefined}
                onMouseEnter={handlePointEnter}
                dataKey="mz"
                r={6}
                data={labeledPoints}
                name="동정 관찰"
                isAnimationActive={false}
                legendType="none"
                activeShape={false}
              >
                {labeledCells}
              </Scatter>
            )}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

MZRTChartRecharts.displayName = "MZRTChartRecharts";
